import { useState, useEffect } from "react";
import { InRow, LoadableContent, PageTitle } from "common/Grid";
import { Form, FormField, PrimaryButton, SecondaryButton } from "common/Forms";
import { DateInput, SelectInput, Checkbox, RadioOptionInput } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";
import { useGetSiteIdOptionsMutation } from "store/action/options-store";
import { toast } from "react-toastify";
import { useUpdateInvoiceDataMutation } from "store/action/invoiceApi";
import { useNavigate } from "react-router-dom";
import { captureComponentError } from "utils/sentry";

const InvoiceForm = () => {
  const [siteIdOptions, setSiteIdOptions] = useState([]);
  const [userInput, setUserInput] = useState({
    fees: ["admin"],
    orderType: "all"
  });
  const [getSiteIdOptionsApi] = useGetSiteIdOptionsMutation();
  const [updateInvoiceData, { isLoading }] = useUpdateInvoiceDataMutation();
  const navigate = useNavigate();

  const handleFeeChange = (feeType, isChecked) => {
    const currentFees = userInput.fees || [];

    if (!isChecked && currentFees.length === 1) {
      toast.warn("At least one fee must be selected");
      return;
    }

    setUserInput(prev => ({
      ...prev,
      fees: isChecked
        ? [...currentFees, feeType]
        : currentFees.filter(fee => fee !== feeType)
    }));
  };

  const isValid =
    userInput?.siteId &&
    userInput?.startDate &&
    userInput?.endDate;

  useEffect(() => {
    async function fetchSiteIdOptions() {
      const response = await getSiteIdOptionsApi();
      const options = response?.data?.data ?? [];
      setSiteIdOptions(options);
    }
    fetchSiteIdOptions();
  }, [getSiteIdOptionsApi]);

  const formatDate = (dateObj) => {
    // If dateObj is a string, convert it to Date object
    const date = typeof dateObj === 'string' ? new Date(dateObj) : dateObj;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const parseDateString = (dateString) => {
    if (!dateString) return null;

    if (dateString instanceof Date) return dateString;

    const parts = dateString.split('-');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }

    return new Date(dateString);
  };

  const handleSubmit = async () => {
    const payload = {
      siteId: userInput.siteId,
      fees: userInput.fees,
      orderType: userInput.orderType,
      startDate: userInput.startDate,
      endDate: userInput.endDate
    };

    try {
      const response = await updateInvoiceData({ body: payload }).unwrap();
      toast.success(response?.message || "Invoice generated successfully");
      navigate("/invoice");
    } catch (error) {
      captureComponentError(error, { component: "InvoiceForm", action: "handleSubmit" });
      toast.error(error?.data?.message || "Something went wrong!");
    }
  };

  return (
    <>
      <LoadableContent isLoading={isLoading}>
        <Form classNames={["mb-10"]}>
          <Grid columns={4} rowGap={1.5} columnGap={2} classNames={["!gap-x-3 !gap-y-3"]}>
            <GridCell columnSpan={4}>
              <PageTitle title="Invoice Form" />
            </GridCell>
            <GridCell columnSpan={2}>
              <FormField
                id="siteid_dropdown"
                label="Site ID"
                required={true}
                inputFactory={(id, name, onValidition) =>
                  <SelectInput
                    key={siteIdOptions.length}
                    disabled={false}
                    id={id}
                    name={name}
                    placeholder="Select site id"
                    selectedKey={userInput?.siteId || ''}
                    optionProvider={() => siteIdOptions}
                    keySelector={(x => `${x.value}`)}
                    isClearable={false}
                    labelSelector={(x => `${x.label}`)}
                    onValidation={onValidition}
                    onChange={(_, n) => {
                      setUserInput(prev => ({
                        ...prev,
                        siteId: n ? n.value : ''
                      }));
                    }}
                  />
                }
              />
            </GridCell>

            <GridCell>
              <FormField
                id="start_date"
                name='Start date'
                label="Start Date"
                required={true}
                inputFactory={(id, name, onValidation) =>
                  <DateInput
                    id={id}
                    name={name}
                    disabled={false}
                    placeholder="Select start date"
                    value={parseDateString(userInput?.startDate)}
                    onValidation={onValidation}
                    onChange={(_, n) => {
                      const startDate = n ? new Date(n) : null;
                      const endDate = userInput?.endDate ? parseDateString(userInput.endDate) : null;

                      if (endDate && startDate && startDate > endDate) {
                        setUserInput(prev => ({ ...prev, startDate: startDate ? formatDate(startDate) : "", endDate: "" }));
                      } else {
                        setUserInput(prev => ({ ...prev, startDate: startDate ? formatDate(startDate) : "" }));
                      }
                    }}
                  />
                }
              />
            </GridCell>

            <GridCell>
              <FormField
                id="end_date"
                name='End date'
                label="End Date"
                required={true}
                inputFactory={(id, name, onValidation) => (
                  <DateInput
                    id={id}
                    name={name}
                    disabled={false}
                    placeholder="Select end date"
                    value={parseDateString(userInput?.endDate)}
                    onValidation={onValidation}
                    onChange={(_, n) => {
                      const endDate = n ? new Date(n) : null;
                      const startDate = userInput?.startDate ? parseDateString(userInput.startDate) : null;

                      if (startDate && endDate && endDate < startDate) {
                        setUserInput(prev => ({ ...prev, endDate: endDate ? formatDate(endDate) : "", startDate: "" }));
                      } else {
                        setUserInput(prev => ({ ...prev, endDate: endDate ? formatDate(endDate) : "" }));
                      }
                    }}
                  />
                )}
              />
            </GridCell>

            <GridCell columnSpan={2} classNames={["mt-4 mb-2"]}>
              <label className="font-bold">Include Fee</label>
            </GridCell>
            <GridCell columnSpan={2} classNames={["mt-4 mb-2"]}>
              <label className="font-bold">Order type</label>
            </GridCell>

            {/* Include Fee */}
            <GridCell columnSpan={1}>
              <InRow gap={1} classNames={['flex-nowrap']}>
                <Checkbox
                  id="admin-fee"
                  value={userInput.fees?.includes("admin")}
                  onChange={(newValue) => handleFeeChange("admin", newValue)}
                />
                <label htmlFor="admin-fee" className="cursor-pointer">Admin Fee</label>
              </InRow>
            </GridCell>
            <GridCell columnSpan={1}>
              <InRow gap={1} classNames={['flex-nowrap']}>
                <Checkbox
                  id="sales-fee"
                  value={userInput.fees?.includes("sales")}
                  onChange={(newValue) => handleFeeChange("sales", newValue)}
                />
                <label htmlFor="sales-fee" className="cursor-pointer">Sales Fee</label>
              </InRow>
            </GridCell>

            {/* Order type */}
            <GridCell columnSpan={1}>
              <InRow gap={1} classNames={['flex-nowrap']}>
                <RadioOptionInput
                  id="all-orders"
                  name="order-type"
                  value={userInput.orderType}
                  optionValue="all"
                  onChange={(val) => setUserInput(prev => ({ ...prev, orderType: val }))}
                />
                <label htmlFor="all-orders" className="cursor-pointer">All Orders</label>
              </InRow>
            </GridCell>
            <GridCell columnSpan={1}>
              <InRow gap={1} classNames={['flex-nowrap']}>
                <RadioOptionInput
                  id="cod-orders"
                  name="order-type"
                  value={userInput.orderType}
                  optionValue="cash"
                  onChange={(val) => setUserInput(prev => ({ ...prev, orderType: val }))}
                />
                <label htmlFor="cod-orders" className="cursor-pointer">Cash</label>
              </InRow>
            </GridCell>
          </Grid>
          <InRow gap={1} classNames={['justify-center mt-8']}>
            <PrimaryButton
              classNames={['min-w-[180px]']}
              disabled={!isValid || isLoading}
              action={handleSubmit}>
              Generate Invoice
            </PrimaryButton>
            <SecondaryButton action={() => navigate("/invoice")}>Cancel</SecondaryButton>
          </InRow>
        </Form >
      </LoadableContent>
    </>
  );
};

export default InvoiceForm;